import { createTheme, responsiveFontSizes } from '@mui/material/styles';

// Create a theme instance with a modern, sophisticated color palette
const theme = createTheme({
  palette: {
    primary: {
      main: '#3a36e0', // Rich indigo - modern and professional
      light: '#6e6ff7',
      dark: '#2a26b9',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#ff5a5f', // Coral - vibrant and engaging
      light: '#ff8a8e',
      dark: '#e5484d',
      contrastText: '#ffffff',
    },
    error: {
      main: '#f44336', // Bright red - clear error indication
      light: '#ff7961',
      dark: '#ba000d',
    },
    success: {
      main: '#00c853', // Vibrant green - positive feedback
      light: '#5efc82',
      dark: '#009624',
    },
    warning: {
      main: '#ffab00', // Amber - attention grabbing
      light: '#ffdd4b',
      dark: '#c67c00',
    },
    info: {
      main: '#03a9f4', // Sky blue - informational
      light: '#67daff',
      dark: '#007ac1',
    },
    grey: {
      50: '#fafafa',  // Lightest grey - for backgrounds
      100: '#f5f5f5',
      200: '#eeeeee',
      300: '#e0e0e0',
      400: '#bdbdbd', // Medium grey - for dividers
      500: '#9e9e9e',
      600: '#757575',
      700: '#616161', // Dark grey - for secondary text
      800: '#424242',
      900: '#212121', // Near black - for primary text
    },
    background: {
      default: '#f8f9ff', // Subtle blue-tinted background for depth
      paper: '#ffffff',
    },
    text: {
      primary: '#212121', // Near black - for primary text
      secondary: '#616161', // Dark grey - for secondary text
    },
  },
  typography: {
    // Modern font pairing with Poppins for headings and Inter for body text
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontFamily: '"Poppins", "Inter", "Roboto", sans-serif',
      fontWeight: 700,
      fontSize: '3.75rem',
      lineHeight: 1.1,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontFamily: '"Poppins", "Inter", "Roboto", sans-serif',
      fontWeight: 700,
      fontSize: '3rem',
      lineHeight: 1.2,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontFamily: '"Poppins", "Inter", "Roboto", sans-serif',
      fontWeight: 600,
      fontSize: '2.25rem',
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    h4: {
      fontFamily: '"Poppins", "Inter", "Roboto", sans-serif',
      fontWeight: 600,
      fontSize: '1.75rem',
      lineHeight: 1.4,
      letterSpacing: '-0.005em',
    },
    h5: {
      fontFamily: '"Poppins", "Inter", "Roboto", sans-serif',
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.5,
      letterSpacing: '-0.005em',
    },
    h6: {
      fontFamily: '"Poppins", "Inter", "Roboto", sans-serif',
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.6,
      letterSpacing: 0,
    },
    subtitle1: {
      fontSize: '1.125rem',
      lineHeight: 1.6,
      fontWeight: 600,
      letterSpacing: '0',
    },
    subtitle2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
      fontWeight: 600,
      letterSpacing: '0',
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.7,
      letterSpacing: '0',
      fontWeight: 400,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.7,
      letterSpacing: '0',
      fontWeight: 400,
    },
    button: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '0.9375rem',
      letterSpacing: '0',
    },
    caption: {
      fontSize: '0.75rem',
      lineHeight: 1.5,
      letterSpacing: '0',
      fontWeight: 400,
    },
    overline: {
      fontSize: '0.75rem',
      fontWeight: 700,
      lineHeight: 1.5,
      letterSpacing: '0.05em',
      textTransform: 'uppercase',
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          padding: '12px 24px', // More generous padding for better touch targets
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          fontWeight: 600,
          textTransform: 'none',
          '&:focus-visible': {
            outline: '2px solid #6e6ff7',
            outlineOffset: '2px',
          },
        },
        contained: {
          boxShadow: '0 4px 14px 0 rgba(58, 54, 224, 0.1)',
          '&:hover': {
            boxShadow: '0 6px 20px rgba(58, 54, 224, 0.2)',
            transform: 'translateY(-3px)',
          },
        },
        containedPrimary: {
          background: 'linear-gradient(45deg, #3a36e0 30%, #6e6ff7 90%)',
          '&:hover': {
            background: 'linear-gradient(45deg, #2a26b9 30%, #5a5be6 90%)',
          },
        },
        containedSecondary: {
          background: 'linear-gradient(45deg, #ff5a5f 30%, #ff8a8e 90%)',
          '&:hover': {
            background: 'linear-gradient(45deg, #e5484d 30%, #ff7478 90%)',
          },
        },
        outlined: {
          borderWidth: '2px',
          '&:hover': {
            borderWidth: '2px',
            backgroundColor: 'rgba(58, 54, 224, 0.04)',
            transform: 'translateY(-2px)',
          },
        },
        text: {
          '&:hover': {
            backgroundColor: 'rgba(58, 54, 224, 0.04)',
            transform: 'translateY(-1px)',
          },
        },
        // Add specific sizes for better flexibility
        sizeLarge: {
          padding: '14px 30px',
          fontSize: '1.125rem',
          fontWeight: 700,
        },
        sizeSmall: {
          padding: '8px 18px',
          fontSize: '0.875rem',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 20, // More rounded corners for modern look
          boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          overflow: 'hidden',
          border: 'none', // Remove border for cleaner look
          background: 'linear-gradient(145deg, #ffffff, #f8f9ff)', // Subtle gradient background
          '&:hover': {
            boxShadow: '0 15px 35px rgba(58, 54, 224, 0.1)',
            transform: 'translateY(-5px)',
          },
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '28px',
          '&:last-child': {
            paddingBottom: '28px',
          },
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          padding: '24px 28px 0',
        },
        title: {
          fontSize: '1.375rem',
          fontWeight: 700,
          fontFamily: '"Poppins", "Inter", "Roboto", sans-serif',
          color: '#212121',
        },
        subheader: {
          fontSize: '0.9375rem',
          color: '#616161', // Updated secondary text color
          marginTop: '4px',
        },
      },
    },
    MuiCardActions: {
      styleOverrides: {
        root: {
          padding: '20px 28px',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          backgroundImage: 'none', // Remove any gradient
          borderBottom: '1px solid rgba(0,0,0,0.05)', // Subtle border instead of shadow
        },
        colorDefault: {
          backgroundColor: '#ffffff',
        },
        colorPrimary: {
          backgroundColor: '#00695C', // Deep Teal
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          padding: '0 24px',
          '@media (min-width: 600px)': {
            padding: '0 32px',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16, // Consistent with cards
          backgroundImage: 'none', // Remove any gradient
        },
        elevation1: {
          boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
        },
        elevation2: {
          boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
        },
        elevation3: {
          boxShadow: '0 6px 20px rgba(0,0,0,0.08)',
        },
        elevation4: {
          boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          margin: '24px 0', // More spacing
          borderColor: 'rgba(0,0,0,0.06)', // Lighter color
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          marginBottom: '16px',
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&.Mui-focused': {
              boxShadow: '0 0 0 4px rgba(58, 54, 224, 0.15)',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#6e6ff7',
            },
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: '#f8f9ff',
          '&:hover': {
            backgroundColor: '#f0f2ff',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#6e6ff7',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#3a36e0',
            borderWidth: 2,
          },
        },
        input: {
          padding: '16px 18px',
          fontSize: '1rem',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: '0.9375rem',
          fontWeight: 500,
          '&.Mui-focused': {
            color: '#3a36e0',
          },
        },
        outlined: {
          transform: 'translate(18px, 18px) scale(1)',
          '&.MuiInputLabel-shrink': {
            transform: 'translate(18px, -8px) scale(0.75)',
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: '#bdbdbd', // Updated unchecked state color
          padding: '9px',
          '&.Mui-checked': {
            color: '#3a36e0', // Primary color for checked state
          },
          '&:hover': {
            backgroundColor: 'rgba(58, 54, 224, 0.04)',
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: '#bdbdbd', // Updated unchecked state color
          padding: '9px',
          '&.Mui-checked': {
            color: '#3a36e0', // Primary color for checked state
          },
          '&:hover': {
            backgroundColor: 'rgba(58, 54, 224, 0.04)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          fontWeight: 500,
          '&.MuiChip-colorPrimary': {
            backgroundColor: 'rgba(0, 105, 92, 0.08)', // Light teal background
            color: '#00695C', // Deep Teal text
          },
          '&.MuiChip-colorSecondary': {
            backgroundColor: 'rgba(255, 143, 0, 0.08)', // Light amber background
            color: '#FF8F00', // Warm Amber text
          },
        },
        deleteIcon: {
          color: 'inherit',
          opacity: 0.7,
          '&:hover': {
            opacity: 1,
          },
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          backgroundColor: '#00695C', // Deep Teal background
          color: '#ffffff', // White text
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.875rem',
          minWidth: 100,
          '&.Mui-selected': {
            color: '#00695C', // Deep Teal
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: '#00695C', // Deep Teal
          height: 3, // Slightly thicker indicator
          borderRadius: '3px 3px 0 0', // Rounded top corners
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '16px 24px', // More padding for better readability
          borderBottom: '1px solid rgba(0,0,0,0.05)', // Lighter border
        },
        head: {
          fontWeight: 600,
          backgroundColor: '#F5F7FA', // Off-white background
          color: '#263238', // Charcoal text
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#263238', // Charcoal background
          color: '#ffffff', // White text
          fontSize: '0.75rem',
          padding: '8px 12px',
          borderRadius: 4,
        },
        arrow: {
          color: '#263238', // Charcoal arrow
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16, // Consistent with cards
          boxShadow: '0 24px 48px rgba(0,0,0,0.2)', // Stronger shadow for modals
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: '24px 24px 16px',
          fontSize: '1.25rem',
          fontWeight: 600,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '16px 24px',
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '16px 24px 24px',
        },
      },
    },
  },
  shadows: [
    'none',
    '0 2px 4px rgba(0,0,0,0.05)',
    '0 4px 8px rgba(0,0,0,0.05)',
    '0 6px 12px rgba(0,0,0,0.08)',
    '0 8px 16px rgba(0,0,0,0.08)',
    '0 10px 20px rgba(0,0,0,0.1)',
    '0 12px 24px rgba(0,0,0,0.1)',
    '0 14px 28px rgba(0,0,0,0.12)',
    '0 16px 32px rgba(0,0,0,0.12)',
    '0 18px 36px rgba(0,0,0,0.14)',
    '0 20px 40px rgba(0,0,0,0.14)',
    '0 22px 44px rgba(0,0,0,0.16)',
    '0 24px 48px rgba(0,0,0,0.16)',
    '0 26px 52px rgba(0,0,0,0.18)',
    '0 28px 56px rgba(0,0,0,0.18)',
    '0 30px 60px rgba(0,0,0,0.2)',
    '0 32px 64px rgba(0,0,0,0.2)',
    '0 34px 68px rgba(0,0,0,0.22)',
    '0 36px 72px rgba(0,0,0,0.22)',
    '0 38px 76px rgba(0,0,0,0.24)',
    '0 40px 80px rgba(0,0,0,0.24)',
    '0 42px 84px rgba(0,0,0,0.26)',
    '0 44px 88px rgba(0,0,0,0.26)',
    '0 46px 92px rgba(0,0,0,0.28)',
    '0 48px 96px rgba(0,0,0,0.28)',
  ],
});

// Make typography responsive
const responsiveTheme = responsiveFontSizes(theme, {
  breakpoints: ['sm', 'md', 'lg', 'xl'],
  factor: 2, // Slightly more aggressive scaling
});

export default responsiveTheme;